import * as React from 'react';
import { RenderHtml } from '../../../helpers/render-html';
import { Logger, withLogger } from '../../../helpers/bi';
import { BIEvent } from '../../../helpers/bi/event-type';
import * as classNames from 'classnames';
import { BUSINESS_EMAIL_LABEL, SUBDOMAIN_LABEL, withTranslator } from '../../../helpers/shared';
import { isNameAvailable, CAPTCHA_SITE_KEY, NewTenantParams } from '../../../helpers/api.client';
import { SelectOption, TinyLoader } from '@wix/answers-ui-libby';
import { MarketingInput, MarketingSelect } from '@wix/answers-common-components';
import { getQueryParamByName, ANSWERS_BASE_DOMAIN, isEmailValid, checkIfMobile, isPassValid } from '../../../helpers/utils';
import * as ReCAPTCHA from 'react-google-recaptcha';
import { RandomGenerator, systemSupportedLanguages } from '@wix/answers-api';
import { TranslateFn } from '@wix/answers-lib';

const beutifySubdomain = (str: string): string => str.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-zA-Z\d-]/g, '');
const addRandomNumberToStr = (val: string): string => `${val}-${RandomGenerator.randomNumber(1000, 9999)}`;
const translatePrefix = 'marketing-site.signup-form.signup';
const ReCaptcha = ReCAPTCHA.default;
const DEFAULT_LOCALE = 'en';

export type MkSignupProps = {
	onSubmit: (params: NewTenantParams & { recaptchaValue: string }) => void;
	prefilledUserData: {
		userName: string;
		userEmail: string;
		organizationName: string;
		prefilledPhoneNumber: string;
	}
} & withLogger & withTranslator;

export type MkSignupState = {
	recaptchaValue: string;
	fullName: string;
	fullNameError: string;
	isFullNamePrefilled: boolean;
	email: string;
	emailError: string;
	isEmailPrefilled: boolean
	password: string;
	passwordError: string;
	isMobile: boolean;
	organizationName: string;
	organizationNameError: string;
	isOrganizationPrefilled: boolean;
	phoneNumber: string;
	phoneNumberError: string;
	isPhoneNumberPrefilled: boolean;
	primaryLocale: string;
	primaryLocaleError: string;
	subdomain: string;
	subdomainError: string;
	nameAvailable: boolean;
	isSubmitting: boolean;
	validating: boolean;
	isSubdomainRetry: boolean;
};

export class MkSignup extends React.PureComponent<MkSignupProps, MkSignupState> {
	logger: Logger = this.props.logger;
	compTranslate: TranslateFn = (key, params) => this.props.t(`${translatePrefix}.${key}`, params);
	requiredError = this.compTranslate('errors.required-field');
	captcha: any = null;
	subdomainCheckTimer: any = null;
	subdomainCheckDelay = 500;

	state: MkSignupState = {
		recaptchaValue: '',
		fullName: this.props.prefilledUserData.userName,
		fullNameError: '',
		isFullNamePrefilled: !!this.props.prefilledUserData.userName,
		email: this.props.prefilledUserData.userEmail,
		emailError: '',
		isEmailPrefilled: !!this.props.prefilledUserData.userEmail,
		password: '',
		passwordError: '',
		isMobile: checkIfMobile(),
		organizationName: this.props.prefilledUserData.organizationName,
		organizationNameError: '',
		isOrganizationPrefilled: !!this.props.prefilledUserData.organizationName,
		phoneNumber: this.props.prefilledUserData.prefilledPhoneNumber,
		phoneNumberError: '',
		isPhoneNumberPrefilled: !!this.props.prefilledUserData.prefilledPhoneNumber,
		primaryLocale: DEFAULT_LOCALE,
		primaryLocaleError: '',
		subdomain: '',
		subdomainError: '',
		nameAvailable: true,
		isSubmitting: false,
		validating: false,
		isSubdomainRetry: false
	};

	componentDidMount () {
		const {organizationName, isOrganizationPrefilled} = this.state;
		if (isOrganizationPrefilled && organizationName) {
			this.setState({subdomain: beutifySubdomain(organizationName), subdomainError: ''}, () => {
				this.validateOrganizationName();
			});
		}
	}

	maxPasswordLength = 6;

	validateName = () => {
		const { compTranslate } = this;
		const fullName = this.state.fullName.trim();
		let result = '';

		if (!fullName) {
			result = compTranslate('errors.name-required');
		} else if (fullName.length < 3) {
			result = compTranslate('errors.name-min-length');
		}

		this.setState({ fullNameError: result });
		return result;
	}

	validateEmail = () => {
		const { compTranslate } = this;
		const email = this.state.email;
		let result = '';

		if (!email) {
			result = compTranslate('errors.email-required');
		} else if (!isEmailValid(email)) {
			result = compTranslate('errors.email-invalid');
		}

		this.setState({ emailError: result });
		return result;
	}

	validatePassword = () => {
		const { compTranslate } = this;
		const pw = this.state.password;
		let result = '';

		if (!pw) {
			result = compTranslate('errors.password-required');
		} else if (pw.length < this.maxPasswordLength) {
			result = compTranslate('errors.password-min-length');
		} else if (!isPassValid(pw)) {
			result = compTranslate('errors.password-rules-unmet');
		}

		this.setState({ passwordError: result });
		return result;
	}

	isValid = () => {
		const nameError = this.validateName();
		const emailError = this.validateEmail();
		const pwdError = this.validatePassword();
		const organizationNameError = this.validateOrganizationName();
		const primaryLocaleError = this.validatePrimaryLocale();
		const subdomainError = this.validateSubdomain();

		return !nameError && !emailError && !pwdError && !organizationNameError && !primaryLocaleError && !subdomainError;
	}

	getInvalidFields = () => {
		return [
			this.validateName() ? 'fullname' : '',
			this.validateEmail() ? 'email' : '',
			this.validatePassword() ? 'password' : '',
			this.validateOrganizationName() ? 'organization-name' : '',
			this.validatePrimaryLocale() ? 'locale' : '',
			this.validateSubdomain() ? 'subdomain' : ''
		]
			.filter((i) => !!i)
			.join(', ');
	}

	isNotEmpty = () => {
		const { state } = this;
		return !!state.fullName && !!state.email && !!state.password && !!state.organizationName && !!state.primaryLocale && !!state.subdomain;
	}

	captchaRef = (ref: any) => this.captcha = ref;

	validateOrganizationName = () => {
		const { compTranslate } = this;
		const name = this.state.organizationName;
		const result = !name.trim().replace(/[^a-zA-Z\d]/g, '') ? compTranslate('errors.organization-required') : '';
		this.setState({ isSubdomainRetry: false, organizationNameError: result });

		if (!result) {
			this.onSubdomainChange(this.state.subdomain);
		}
		return result;
	}

	validatePrimaryLocale = () => {
		const { compTranslate } = this;
		const locale = this.state.primaryLocale;
		const result = !locale ? compTranslate('errors.primary-locale') : '';

		this.setState({ primaryLocaleError: result });
		return result;
	}

	validateSubdomain = () => {
		const { compTranslate } = this;
		const sub = this.state.subdomain;
		const pattern = /^[A-Za-z0-9](?:[A-Za-z0-9\-]{0,61}[A-Za-z0-9])?$/;
		let result = '';

		if (!sub) {
			result = compTranslate('errors.subdomain-required');
		} else if (sub.length < 4) {
			result = compTranslate('errors.subdomain-min-length');
		} else if (!pattern.test(sub)) {
			result = compTranslate('errors.subdomain-invalid');
		} else if (!this.state.nameAvailable) {
			result = compTranslate('errors.subdomain-taken');
		}

		this.setState({ subdomainError: result });
		return result;
	}

	parsePossiblePhoneNumber = (phoneNumber: string) => {
		return phoneNumber.trim().length ? {
			phoneNumber: {
				countryCode: '',
				number: phoneNumber.trim()
			}
		} : {};
	}

	organizationNameOnChange = (val: string) => this.setState({ organizationName: val, organizationNameError: '', subdomain: beutifySubdomain(val), subdomainError: '' });

	onLocaleChange = (option: SelectOption) => this.setState({ primaryLocale: option.value, primaryLocaleError: '' }, this.validatePrimaryLocale);

	onPhoneNumberChange = (val: string) => this.setState({ phoneNumber: val, phoneNumberError: '' });

	onSubdomainChange = (val: string) => {
		this.setState({ subdomain: val, subdomainError: '' }, () => {
			clearTimeout(this.subdomainCheckTimer);

			this.setState({ nameAvailable: true });

			const error = this.validateSubdomain();
			if (error && !!val && !this.state.isSubdomainRetry) {
				const modifiedSubdomain = addRandomNumberToStr(this.state.subdomain);
				this.setState({ subdomain: modifiedSubdomain }, () => {
					this.onSubdomainChange(modifiedSubdomain);
				});
			}
			if (!error) {
				this.subdomainCheckTimer = setTimeout(() => {
					this.setState({ validating: true });
					isNameAvailable(val).then((res) => {
							if (!res) {
								this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, { subdomain: val, error: 'Subdomain taken' });
								if (!this.state.isSubdomainRetry) {
									this.setState({ isSubdomainRetry: true, validating: false }, () => {
										this.onSubdomainChange(addRandomNumberToStr(this.state.subdomain));
									});
								}
							}
							if (res) {
								this.setState({ nameAvailable: res, validating: false }, () => {
									this.setState({ subdomainError: this.validateSubdomain() });
								});
							}
						}, () => this.setState({ validating: false }));
				}, this.subdomainCheckDelay);
			}
		});
	}

	onSubmit = (e: any) => {
		const { state, logger } = this;

		e.stopPropagation();
		e.preventDefault();

		if (state.isMobile) {
			this.setState({ isMobile: false });
			return;
		}

		if (state.validating || state.isSubmitting) {
			return;
		}

		const params: Partial<NewTenantParams> = {
			userEmail: state.email,
			userPassword: state.password,
			userName: state.fullName,
			organizationName: state.organizationName,
			locale: state.primaryLocale,
			subdomain: state.subdomain,
			...this.parsePossiblePhoneNumber(state.phoneNumber)
		};

		logger.logEvent(BIEvent.SIGNUP_CLICK_STEP2, { subdomain: params.subdomain });

		if (!!this.captcha && this.isValid()) {
			this.setState({ isSubmitting: true });

			if (!(window as any).__isProduction) {
				this.onCaptchaResolve('');
			} else {
				this.captcha.execute();
			}
		} else {
			const invalidFields = this.getInvalidFields();
			logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, { ...params, error: `Form validation failed in fields - [${invalidFields}]` });
		}
	}

	onCaptchaResolve = (recaptchaValue: string) => {
		const { state } = this;

		const params = {
			userEmail: state.email,
			userPassword: state.password,
			userName: state.fullName,
			subdomain: state.subdomain,
			locale: getQueryParamByName('overrideLocale') || state.primaryLocale,
			organizationName: state.organizationName,
			recaptchaValue,
			...this.parsePossiblePhoneNumber(state.phoneNumber)
		};
		this.props.onSubmit(params);
	}

	localesOptions = (): SelectOption[] => systemSupportedLanguages.map((lang) => {
		const isSelectedClass = this.state.primaryLocale === lang ? ' is-selected' : '';

		return { label: this.props.t(`common.lang.${lang}`), value: lang, className: `option-${lang}${isSelectedClass}` };
	})

	fullNameOnChange = (val: string) => this.setState({ fullName: val, fullNameError: '' });
	emailOnChange = (val: string) => this.setState({ email: val, emailError: '' });
	passwordOnChange = (val: string) => this.setState({ password: val.trim(), passwordError: '' });

	render () {
		const { state, props, compTranslate } = this;
		const currentLocale = state.primaryLocale ? { label: props.t(`common.lang.${state.primaryLocale}`), value: state.primaryLocale } : null;
		const localeOptions = this.localesOptions();
		const isRequiredLabel = (label: string): string => `${compTranslate(label)}*`;

		const formComp = (
			<div className='mk-signup-form'>
				<form className='form-container' noValidate={true} onSubmit={this.onSubmit}>
					<MarketingInput className='fullname' error={state.fullNameError} value={state.fullName} type='text' label={isRequiredLabel('fields.name')} onChange={this.fullNameOnChange} onBlur={this.validateName} startFocused={true} />
					<MarketingInput className='email' error={state.emailError} value={state.email} type='email' label={BUSINESS_EMAIL_LABEL + '*'} onChange={this.emailOnChange} onBlur={this.validateEmail} />
					<MarketingInput className='organization' error={state.organizationNameError} value={state.organizationName} type='text' label={isRequiredLabel('fields.organization-name')} onChange={this.organizationNameOnChange} onBlur={this.validateOrganizationName} />
					<MarketingInput className='subdomain' maxLength={80} fixedPlaceholder={ANSWERS_BASE_DOMAIN} error={state.subdomainError} value={state.subdomain} type='text' label={SUBDOMAIN_LABEL} onChange={(this.onSubdomainChange)} disabledText={'Your Wix Answers subdomain is based on the company name.'} />
					<MarketingInput className='pass' error={state.passwordError} value={state.password} type='password' label={isRequiredLabel('fields.password')} onChange={this.passwordOnChange} onBlur={this.validatePassword} />
					<MarketingSelect className='locale' error={state.primaryLocaleError} value={currentLocale} options={localeOptions} label={isRequiredLabel('fields.primary-locale')} onChange={this.onLocaleChange} />
					<div className={`signup-button-container ${state.isSubmitting ? 'submitting' : ''}`}><span className='signing-up-msg'>{state.isSubmitting ? compTranslate('signin-up') : null}</span>
						<button className='primary-btn' onClick={this.onSubmit}>
							{state.isSubmitting ? <TinyLoader /> : compTranslate('cta-mk')}
						</button>
					</div>
				</form>
			</div>
		);

		return (
			<div className={classNames('mk-signup-container')} >
				<header>
					<h1>{compTranslate('title-mk')}</h1>
				</header>
				<main className='mk-signup-form-container'>{formComp}</main>
				<div id='signup-terms'><RenderHtml html={compTranslate('new-footer')} /></div>
				<ReCaptcha
					className='recaptcha-container'
					ref={this.captchaRef}
					size='invisible'
					onChange={this.onCaptchaResolve}
					sitekey={CAPTCHA_SITE_KEY}
					badge='bottomright'
				/>
			</div>
		);
	}
}
